import { action, computed, makeObservable, observable } from 'mobx'

import { getCatalogs } from '../firestore/Catalog'
import {
  getSettingsProcessingVideo,
  uploadSettingsProcessingVideo,
} from '../firestore/SettingsProcessingVideo'
import { createSlideDeck, getSlideDecks } from '../firestore/SlideDeck'
import { Catalog } from '../models/Catalog'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SettingsProcessingVideo } from '../models/SettingsProcessingVideo'
import { SlideDeck, SlideDeckState } from '../models/SlideDeck'
import { SlideDeckGroup } from '../stores/SlideDeckGroup'
import type { StaticModelCollection } from '../types'
import { Cubit } from './core'
import { SettingsSlideDeckTag } from '../models/SettingsSlideDeckTag'
import {
  getSettingsSlideDeckTags,
  hideSettingsSlideDeckTag,
  saveSettingsSlideDeckTags,
} from '../firestore/SettingsSlideDeckTag'
import { httpsCallable } from 'firebase/functions'
import { captureException } from '@sentry/core'
import { FirebaseError } from 'firebase/app'

export class AdminLibraryCubit extends Cubit {
  repository: FirebaseRepository

  slideDecks: StaticModelCollection<SlideDeck>
  catalogs: StaticModelCollection<Catalog>
  @observable private _filters: string[] = []

  settingsProcessingVideo: SettingsProcessingVideo
  private _settingsSlideDeckTags: StaticModelCollection<SettingsSlideDeckTag>

  @observable showHidden = false

  constructor(repository: FirebaseRepository) {
    super()
    makeObservable(this)

    this.repository = repository
    this.slideDecks = SlideDeck.emptyCollection(repository)
    this.catalogs = Catalog.emptyCollection(repository)
    this.settingsProcessingVideo = SettingsProcessingVideo.empty(repository)
    this._settingsSlideDeckTags =
      SettingsSlideDeckTag.emptyCollection(repository)
  }

  initialize(): void {
    this.addStream(getSlideDecks(this.repository), (rooms) => {
      this.slideDecks.replaceModels(rooms)
    })
    this.addStream(getCatalogs(this.repository), (catalogs) => {
      this.catalogs.replaceModels(catalogs)
    })
    this.addStream(
      getSettingsProcessingVideo(this.repository),
      (settingsProcessingVideo) => {
        this.settingsProcessingVideo.replaceModel(settingsProcessingVideo)
      }
    )
  }

  @action
  addFilter = (filter: string) => {
    this._filters.push(filter.toLowerCase())
  }

  @action
  removeFilter = (filter: string) => {
    this._filters = this._filters.filter((f) => f !== filter)
  }

  @computed
  get filters() {
    return this._filters
  }

  @computed
  get slideDecksGroups() {
    const grouped = new Map<string, SlideDeckGroup>()

    // First filter by hidden state
    const hiddenFiltered = this.showHidden
      ? this.slideDecks.models
      : this.slideDecks.models.filter((slideDeck) => {
          return slideDeck.data.slideDeckState !== SlideDeckState.hidden
        })

    // Then apply filters if there are any
    const filtered =
      this._filters.length > 0
        ? hiddenFiltered.filter((slideDeck) => {
            const name = slideDeck.data.slideDeckName?.toLowerCase() || ''
            const teaser = slideDeck.data.slideDeckTeaser?.toLowerCase() || ''

            // Check if all filters match either the name or teaser
            return this._filters.every(
              (filter) => name.includes(filter) || teaser.includes(filter)
            )
          })
        : hiddenFiltered

    // Group the filtered results
    filtered.forEach((slideDeck) => {
      const parentId = slideDeck.data.slideDeckTypeId || slideDeck.id
      let group = grouped.get(parentId)

      if (!group) {
        group = new SlideDeckGroup(parentId, true)
        grouped.set(parentId, group)
      }

      group.addSlideDeck(slideDeck)
    })

    // Sort groups by updated time
    return Array.from(grouped.values()).sort((a, b) => {
      return b.updatedAt.getTime() - a.updatedAt.getTime()
    })
  }

  @computed
  get settingsSlideDeckTags() {
    const streamKey = 'settingsSlideDeckTags'
    if (!this.hasStream(streamKey)) {
      this.addStream(
        getSettingsSlideDeckTags(this.repository),
        (settingsSlideDeckTags) => {
          this.settingsSlideDeckTags.replaceModels(settingsSlideDeckTags)
        },
        {
          name: streamKey,
        }
      )
    }
    return this._settingsSlideDeckTags
  }

  /**
   * Add a new slide deck to the library
   *
   * @returns string - The ID of the newly created slide deck
   */
  addSlideDeck(): Promise<string> {
    return createSlideDeck(this.repository)
  }

  @action
  toggleShowHidden = () => {
    this.showHidden = !this.showHidden
  }

  uploadSettingsProcessingVideo = async (file: File) => {
    return uploadSettingsProcessingVideo(this.repository, file)
  }

  saveSettingsSlideDeckTags = async (
    params: Parameters<typeof saveSettingsSlideDeckTags>[1]
  ) => {
    return await saveSettingsSlideDeckTags(this.repository, {
      ...params,
    })
  }

  hideSettingsSlideDeckTag = async (
    params: Parameters<typeof hideSettingsSlideDeckTag>[1]
  ) => {
    return await hideSettingsSlideDeckTag(this.repository, {
      ...params,
    })
  }

  triggerEmailTemplateRun = async (
    kind:
      | 'dailySessionReminder'
      | 'roomStateMessage'
      | 'sessionSoonReminder'
      | 'sessionResultsAvailable'
  ) => {
    const callFunc = httpsCallable<string, Record<string, unknown>[]>(
      this.repository.functions,
      'invokeEmailTemplateRunForSuperAdminV1'
    )
    try {
      const { data } = await callFunc(kind)

      return {
        success: true,
        message: data.length
          ? `${data.length} emails sent`
          : `No emails to send`,
        results: data,
      }
    } catch (e) {
      captureException(e)
      const msg = e instanceof FirebaseError ? e.message : e
      return {
        success: false,
        message: `Error invoking email template run:, ${msg}`,
        results: [],
      }
    }
  }
}
